import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { type Venue } from 'generated/graphql';
import { useApolloClient } from '@apollo/client';
import { ProductFragment as PRODUCT_FRAGMENT } from 'queries/Product';
import { sanitiseUrlParam, useAvailabilityTypeByVariablesByApp } from 'utils';

import Image from 'components/Image/Image';
import { SelectMenu } from 'components/SelectMenu';
import { type Availability } from 'types';
import AvailabilityPill from 'components/AvailabilityPill/AvailabilityPill';
import { availabilities } from 'utils/constants';
import { useSetAvailability } from 'utils/Availability';

import { useModalContext } from 'common/Context/ModalContext';

import SnackbarOutlet from 'components/Snackbar/SnackbarOutlet';
import IconViewPosIds from 'icons/viewPosIds.svg';
import General from './General';
import OptionGroups from './OptionGroups';

import styles from './Details.module.scss';

interface Props {
  currentVenue?: Venue | undefined | null;
  dietaryEnums: string[];
}

const Details: React.FC<Props> = ({ currentVenue, dietaryEnums }) => {
  const { itemId } = useParams();

  const sanitisedItemId = sanitiseUrlParam(itemId);

  const { openModal } = useModalContext();

  const client = useApolloClient();

  const productCacheKey = client.cache.identify({ __typename: 'Product', mmId: sanitisedItemId });

  if (!productCacheKey) {
    // eslint-disable-next-line no-console
    console.error(`Unable to identify Product with mmId: ${sanitisedItemId}`);
  }

  const item = productCacheKey
    ? client.readFragment(
        {
          id: productCacheKey,
          fragment: PRODUCT_FRAGMENT,
          fragmentName: 'ProductFragment'
        },
        true
      )
    : null;

  const { name, imageUri, posId, options, variablesByApp } = item || {};

  const availabilityType = useAvailabilityTypeByVariablesByApp(variablesByApp || []);

  const [view, setView] = useState<string>('general');

  const [handleAvailabilityChange, availabilityChangeLoading] = useSetAvailability(item, currentVenue, true);

  const location = useLocation();
  const navigate = useNavigate();

  const closeModal = (): void => {
    const { pathname } = location;
    const pathnameWithoutHash = pathname.split('/items')[0];

    navigate(pathnameWithoutHash);
  };

  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  return (
    <div className={styles.Details} data-testid="details-container-v1" aria-modal="true">
      <button
        className={styles.Close}
        type="button"
        onClick={closeModal}
        data-testid="details-close-button"
        ref={closeRef}
      >
        Close
      </button>
      <section>
        <div className={styles.CornerContainer}>
          <SelectMenu
            options={[
              {
                content: (
                  <span>
                    <img src={IconViewPosIds} alt="" className={styles.Icon} /> Show POS IDs
                  </span>
                ),
                value: 'show-pos-ids',
                type: 'button',
                callback: () => {
                  openModal({
                    type: 'POS_DEFAULTS',
                    menuItem: item
                  });
                }
              }
            ]}
            defaultButtonContent={<i className="mm-ellipsis-v" />}
            buttonStyle={styles.CloseButton}
            downIcon={null}
            align="right"
            size="small"
          />
        </div>
        <div className={styles.Intro}>
          <div className={styles.Image}>
            <Image alt={name} src={imageUri} />
          </div>
          <div>
            <h3 data-testid="details-name">{name}</h3>
            <SelectMenu
              options={availabilities.map((availability: Availability) => ({
                content: (
                  <>
                    <i className={`Dot ${availability.slug}`} />
                    <span>{availability.name}</span>
                  </>
                ),
                value: availability.slug,
                type: 'button',
                hidden: availability.slug === 'mixed' && availabilityType !== 'mixed'
              }))}
              defaultButtonContent={
                <AvailabilityPill loading={availabilityChangeLoading} availability={availabilityType} />
              }
              downIcon={null}
              onChange={handleAvailabilityChange}
              buttonStyle={styles.Price}
              align="right"
              size="small"
            />
          </div>
        </div>
        <ul className={styles.Tabs}>
          <li className={view === 'general' ? styles.Active : ''}>
            <button
              onClick={() => setView('general')}
              data-testid="details-general-button"
              type="button"
              tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
            >
              General
            </button>
          </li>
          {options && options.length > 0 && (
            <li className={view === 'optionGroups' ? styles.Active : ''}>
              <button
                onClick={() => setView('optionGroups')}
                data-testid="details-optionGroups-button"
                type="button"
                tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
              >
                Option Groups
              </button>
            </li>
          )}
        </ul>
        <General item={item} visible={view === 'general'} dietaryEnums={dietaryEnums} />
        <OptionGroups item={item} visible={view !== 'general'} />
        <div className={styles.PosID}>POS ID {posId}</div>
        <SnackbarOutlet itemId={item?.mmId} />
      </section>
    </div>
  );
};

export default Details;
